import { inject } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Store } from "@inferno/renderer-shared-core";
import { createGoogleTranslator } from "../lib/translator";

const log = ILog.logger("CoastLanguageSelector");

interface CoastLanguageSelectorProps {
  store?: Store;
}

const GOOGLE_TRANSLATE_CONTAINER = "coast-google-translate-container";

export const CoastLanguageSelector = inject("store")(({ store }: CoastLanguageSelectorProps) => {
  const [isInitialized, setIsInitialized] = useState(false);

  const browserLanguage = useMemo(() => {
    if (typeof navigator === "undefined") return "en";
    const lang = (navigator.languages && navigator.languages[0]) || navigator.language || "en";
    return lang.split("-")[0];
  }, []);

  const translator = useMemo(() => {
    const scriptUrl = store?.env?.GOOGLE_TRANSLATE_SCRIPT_URL;
    if (!scriptUrl) return null;

    const actualPageLanguage =
      typeof document !== "undefined" ? document.documentElement.lang?.split("-")[0] || "en" : "en";
    return createGoogleTranslator(
      scriptUrl,
      {
        pageLanguage: actualPageLanguage,
        autoTranslateTo: browserLanguage,
      },
      {
        onReady: () => setIsInitialized(true),
        onError: error => log.error("Google Translate initialization failed:", error),
      },
    );
  }, [store?.env?.GOOGLE_TRANSLATE_SCRIPT_URL, browserLanguage]);

  const initializeTranslate = useCallback(async () => {
    if (isInitialized || !translator) return;
    try {
      await translator.initialize();
      translator.createTranslateElement(GOOGLE_TRANSLATE_CONTAINER);
    } catch (err) {
      log.error("Translation setup failed:", err);
    }
  }, [translator, isInitialized]);

  useEffect(() => {
    initializeTranslate();
  }, [initializeTranslate]);

  useEffect(() => {
    if (!isInitialized) return;

    const container = document.getElementById(GOOGLE_TRANSLATE_CONTAINER);
    if (!container) return;

    const removeDownArrow = () => {
      const elements = container.querySelectorAll(
        "span[aria-hidden], span[style*='color:rgb(155'], span[style*='border-left'], span[style*='border-right'], img[src*='cleardot']",
      );
      elements.forEach(e => {
        const el = e as HTMLElement;
        if (el.textContent?.includes("▼") || el.style.borderLeft || el.style.borderRight || el.tagName === "IMG") {
          el.style.display = "none";
        }
      });
    };

    const removeTopBannerIframe = () => {
      const bannerIframes = document.querySelectorAll<HTMLIFrameElement>(
        "iframe.VIpgJd-ZVi9od-ORHb-OEVmcd.skiptranslate",
      );
      bannerIframes.forEach(iframe => {
        iframe.style.visibility = "hidden";
      });
    };

    const preventNavigationHandler = (event: Event) => event.preventDefault();
    const disableLinkNavigation = () => {
      const links = container.querySelectorAll<HTMLAnchorElement>(".VIpgJd-ZVi9od-xl07Ob-lTBxed");
      links.forEach(link => {
        link.setAttribute("href", "javascript:void(0);");
        if (!link.dataset.preventNavigation) {
          link.addEventListener("click", preventNavigationHandler);
          link.dataset.preventNavigation = "true";
        }
      });
    };

    const styleDropdown = () => {
      const iframes = document.querySelectorAll<HTMLIFrameElement>("iframe.VIpgJd-ZVi9od-xl07Ob-OEVmcd");
      iframes.forEach(iframe => {
        try {
          iframe.style.boxShadow = "none";
          iframe.style.filter = "none";

          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc) return;

          const indicators = doc.querySelectorAll(".indicator");
          indicators.forEach(el => el.remove());

          const langLinks = doc.querySelectorAll(".VIpgJd-ZVi9od-vH1Gmf-ibnC6b");
          langLinks.forEach(el => {
            el.classList.remove("VIpgJd-ZVi9od-vH1Gmf-ibnC6b");
            el.classList.add("VIpgJd-ZVi9od-vH1Gmf-ibnC6b-gk6SMd");
          });

          const textSpans = doc.querySelectorAll(".text");
          textSpans.forEach(el => {
            const span = el as HTMLElement;
            span.style.color = "white";
            span.style.fontWeight = "100";
            span.style.fontSize = "16px";
          });

          if (!doc.getElementById("custom-hover-style")) {
            const style = doc.createElement("style");
            style.id = "custom-hover-style";
            style.innerHTML = `
          .text:hover {
            color: #4aabff !important;
            cursor: pointer;
          }
        `;
            doc.head.appendChild(style);
          }

          const menu = doc.querySelector(".VIpgJd-ZVi9od-vH1Gmf") as HTMLElement;
          if (menu) {
            Object.assign(menu.style, {
              backgroundColor: "black",
              color: "white",
              borderRadius: "0px",
              border: "none",
              padding: "8px 14px",
            });
          }
        } catch (err) {
          log.error("Failed to style Google Translate dropdown:", err);
        }
      });
    };

    const enableHoverDropdown = () => {
      const triggerLink = document.querySelector<HTMLAnchorElement>(".VIpgJd-ZVi9od-xl07Ob-lTBxed");
      if (!triggerLink) return;

      try {
        triggerLink.click();

        const iframe = document.querySelector<HTMLIFrameElement>("iframe.VIpgJd-ZVi9od-xl07Ob-OEVmcd");
        if (!iframe) return;

        const hideIframe = () => Object.assign(iframe.style, { display: "none", visibility: "hidden" });
        const showIframe = () => Object.assign(iframe.style, { display: "block", visibility: "visible" });

        hideIframe();

        triggerLink.addEventListener("mouseenter", showIframe);
        triggerLink.addEventListener("mouseleave", () => !iframe.matches(":hover") && hideIframe());
        iframe.addEventListener("mouseleave", hideIframe);
      } catch (err) {
        log.error("Hover dropdown setup failed:", err);
      }
    };

    const observer = new MutationObserver(() => {
      removeDownArrow();
      disableLinkNavigation();
      styleDropdown();
      removeTopBannerIframe();
      enableHoverDropdown();
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      const links = container.querySelectorAll<HTMLAnchorElement>(".VIpgJd-ZVi9od-xl07Ob-lTBxed");
      links.forEach(link => {
        if (link.dataset.preventNavigation) {
          link.removeEventListener("click", preventNavigationHandler);
          delete link.dataset.preventNavigation;
        }
      });
    };
  }, [isInitialized]);

  if (!store) return null;

  return <div id={GOOGLE_TRANSLATE_CONTAINER} />;
});
